import SignalingService from "./SignalingService";
import LiveStreaming from "./LiveStreaming";
import PeerConnectionManager from "./PeerConnectionManager";
import Utils from "./utils/Utils";
import LoggerService from "./LoggerService";

import { CONNECTION_STATES } from "./enums/LiveStreamingEnums";
import ConnectionManager from "./ConnectionManager";

export class RequestManager {
  
  static allRequests = {};
  /**@type {Array<Request>} */
  static pendingRequestQueue = [];
  static processingIntervalDurationInMilliSeconds = 1000;
  static isActive = false;
  /**@type {Set<Request>} */
  // static closedRequests = new Set(); // Set to track closed requests due to internet loss

  static init() {
    if (LiveStreaming.config.streamingRequestTimeoutInSeconds > 0) {
      Request.setRequestTimeoutDuration(LiveStreaming.config.streamingRequestTimeoutInSeconds);
    }
  }

  static start() {
    RequestManager.isActive = true;
    RequestManager.allRequests = {};
    RequestManager.pendingRequestQueue = [];
    RequestManager.startProcessingPendingRequests();
  }

  static stop() {
    RequestManager.isActive = false;
    RequestManager.allRequests = {};
    RequestManager.stopProcessingPendingRequests();
  }

  static getRequestId(config) {
    const { userId, requestedStreams } = config;
    const mediaType = Utils.getMediaType(requestedStreams);
    return  Utils.getPeerId(userId, mediaType);
  }

  static async createNewRequest(config) {
    
    const requestId = RequestManager.getRequestId(config);

    if (RequestManager.checkRequestExists(requestId)) {

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [peerId: ${requestId}] [Request ignored - already exists]`
      );
      
      return;
    }

    const request = new Request(config);

    RequestManager.addToAllRequests(request);

    RequestManager.addToPendingRequestQueue(request);
  }

  static async createRetryRequest(config) {
    const requestId = RequestManager.getRequestId(config);

    if (RequestManager.checkRequestExists(requestId)) {
      const request = RequestManager.getRequest(requestId);
      
      RequestManager.addToPendingRequestQueue(request);
    }
  }

  static isRequestBeingProcessed(request) {
    if (RequestManager.checkRequestExists(request.requestId)) {
      return RequestManager.getRequest(request.requestId).isProcessing;
    }
    return false;
  }

  static getRequest(requestId)
  {
    return /**@type {Request}*/(RequestManager.allRequests[requestId]);
  }

  static async closeRequest(config) {

    const requestId = RequestManager.getRequestId(config);

    if (RequestManager.checkRequestExists(requestId)) {
      let request = /**@type {Request}*/(RequestManager.getRequest(requestId));
      RequestManager.removeFromAllRequests(request);
      request.close();
      // RequestManager.closedRequests.delete(request);
    }
  }

  static clearConnectionTimer(config) {
    const requestId = RequestManager.getRequestId(config);

    if (RequestManager.checkRequestExists(requestId)) {
      let request = /**@type {Request}*/(RequestManager.getRequest(requestId));
      request.clear();
    }
  }

  static checkRequestExists(requestId) {
    return !!RequestManager.allRequests[requestId];
  }

  static requestExists(request) {
    const requestId = request.requestId;
    return !!RequestManager.allRequests[requestId];
  }

  static requestExistsInPendingRequestQueue(currentRequest) {
    for (let request of RequestManager.pendingRequestQueue) {
      if (request.requestId == currentRequest.requestId) {
        return true;
      }
    }

    return false;
  }

  static addToAllRequests(request) {
    const requestId = request.requestId;
    RequestManager.allRequests[requestId] = request;

    LoggerService.info(
      LoggerService.MODULE.STREAMING,
      `[Streaming] [peerId: ${requestId}] [NEW REQUEST CREATED] [total: ${Object.keys(RequestManager.allRequests).length}]`
    );
  }

  static removeFromAllRequests(request) {
    const requestId = request.requestId;
    delete RequestManager.allRequests[requestId];

    LoggerService.info(
      LoggerService.MODULE.STREAMING,
      `[Streaming] [peerId: ${requestId}] [REQUEST REMOVED] [total: ${Object.keys(RequestManager.allRequests).length}]`
    );
  }

  static addToPendingRequestQueue(request) {
    // return if request does not exist in all requests
    if (!RequestManager.checkRequestExists(request.requestId)) {
      return;
    }

    // return if request already exists in pending queue
    if (RequestManager.requestExistsInPendingRequestQueue(request)) {
      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [peerId: ${request.requestId}] [Request not added to QUEUE] [already exists]`
      );
      return;
    }

    if (RequestManager.isRequestBeingProcessed(request)) {
      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [peerId: ${request.requestId}] [Request not added to QUEUE] [already in processing state]`
      );
      return;
    }

    // return if request is already processed
    if (request.isProcessed()) {
      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [peerId: ${request.requestId}] [Request not added to QUEUE] [already processed]`
      );
      return;
    }

    RequestManager.pendingRequestQueue.push(request);

    LoggerService.info(
      LoggerService.MODULE.STREAMING,
      `[Streaming] [peerId: ${request.requestId}] [Queue length: ${RequestManager.pendingRequestQueue.length}] [REQUEST ADDED TO QUEUE]`
    );
  }

  static removeFromPendingRequestQueue(incomingRequest) {
    RequestManager.pendingRequestQueue = RequestManager.pendingRequestQueue.filter(
      (request) => request.config == incomingRequest.config
    );
  }

  static async initRequest(request) { 
    try {
      if (ConnectionManager.isCurrentUserConnected()) {
        await request.init();
        return true
      } else {
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  /**
   * this method is invoked when an offer is received from the candidate
   */
  static notifyOfferReceived(config) { 
    const {
      peerId
    } = config;

    const requestId = peerId;
    
    if (RequestManager.checkRequestExists(requestId)) {
      RequestManager.getRequest(requestId).setOfferRecievedStatus(true);
    }
  }

  static async processRequest(request) {
    if (RequestManager.checkRequestExists(request.requestId)) {
      
      // return if request is already being processed
      if (RequestManager.isRequestBeingProcessed(request))
      {
        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [peerId: ${request.requestId}] [Request ignored - already in processing]`
        );
        return;
      }

      let bSuccess = await RequestManager.initRequest(request);
      
      if (!bSuccess) 
      {
        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [peerId: ${request.requestId}] [Request init failed - request added to queue]`
        );
        RequestManager.addToPendingRequestQueue(request);
        return;
      }
      
      let isRequestProcessed = false;
      
      if (RequestManager.getRequest(request.requestId)) {
        isRequestProcessed = await RequestManager.getRequest(request.requestId).process();
      }

      // if request is still valid but not processed add it back to queue
      if (RequestManager.checkRequestExists(request.requestId)) {

        if (!isRequestProcessed)  {

          LoggerService.info(
            LoggerService.MODULE.STREAMING,
            `[Streaming] [peerId: ${request.requestId}] [REQUEST ADDED IN QUEUE]`
          );

          RequestManager.addToPendingRequestQueue(request);
        }
      }
    }
  }

  static startProcessingPendingRequests() {
    try {
      if (!RequestManager.isActive) {
        return;
      }

      RequestManager.processingInterval = setInterval(() => {

        if (ConnectionManager.isCurrentUserConnected() && RequestManager.pendingRequestQueue.length > 0) {
          
          // LoggerService.info(
          //   LoggerService.MODULE.STREAMING,
          //   `[Streaming] [QUEUE processing ${RequestManager.pendingRequestQueue.length} requests]`
          // );

          for (let request of RequestManager.pendingRequestQueue) {
            RequestManager.processRequest(request);
          }

          RequestManager.pendingRequestQueue = [];
        }
      }, RequestManager.processingIntervalDurationInMilliSeconds);
    } catch (error) {
      console.log(error);
    }
  }

  static stopProcessingPendingRequests() {
    RequestManager.pendingRequestQueue = [];
    clearInterval(RequestManager.processingInterval);
  }
}

export class Request {
  static requestTimeoutInSeconds = 30;
  static offerWaitTimeoutInSeconds = 10;

  constructor(config) {
    this.config = config;
    this.instance = SignalingService.getInstance();

    const { userId, requestedStreams } = this.config;

    this.userId = userId;
    this.requestedStreams = requestedStreams;
    this.mediaType = Utils.getMediaType(requestedStreams);
    this.peerId = Utils.getPeerId(this.userId, this.mediaType);
    this.requestId = this.peerId;
    this.isProcessing = false;
    this.isOfferReceived = false;
    this._requestExecutionPromiseResolve = null;
    this._waitForOfferPromiseResolve = null;
    this.delayTimeoutId = null;
  }

  setProcessingStatus(status) {
    this.isProcessing = status;

    if (this.isProcessing) {
      this.setOfferRecievedStatus(false);
    }
  }

  async init() {
    try {
      await this.instance.joinRoom(this.userId);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  async close() {
    try {
      const config = {
        launchId: LiveStreaming.config.launchId,
        roomId: this.userId,
        peerId: this.peerId
      };

      this.clear();
      PeerConnectionManager.removePeerConnection(this.peerId);
      this.instance.sendMediaStreamingStopRequest(config, this.requestedStreams);
    } catch (error) {
      console.log(error);
    }
  }

  static setRequestTimeoutDuration(timeInSeconds) {
    Request.requestTimeoutInSeconds = timeInSeconds;
  }

  setOfferRecievedStatus(status) {
    this.isOfferReceived = status;

    if (this.isOfferReceived && this._waitForOfferPromiseResolve) {
      this._waitForOfferPromiseResolve(true);
    }
  }

  async process() {
    try {
      if (this.isProcessed()) {
        return true;
      }

      // check processing status
      const isProcessed = await this.execute();

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [peerId: ${this.requestId}] [REQUEST IS COMPLETED] [${isProcessed ? "SUCCESS": "FAILED"}]`
      );

      // return processing status
      return isProcessed;
    } catch (error) {
      this.setProcessingStatus(false);
      return false;
    }
  }

  isProcessed() {
    const connectionState = PeerConnectionManager.getConnectionState(this.peerId);
    let isProcessed = connectionState == CONNECTION_STATES.Connected;

    return isProcessed;
  }

  async waitForOfferTobeReceived() {

    LoggerService.info(
      LoggerService.MODULE.STREAMING,
      `[Streaming] [peerId: ${this.peerId}] [Waiting for OFFER]`
    );

    return new Promise((resolve) => {
      // this reference to prmise 
      this._waitForOfferPromiseResolve = resolve;

      setTimeout(() => {
        resolve(this.isOfferReceived)
      }, Request.offerWaitTimeoutInSeconds* 1000)
    })
  }

  execute() {
    return new Promise(async (resolve) => {
      // Store resolve so we can externally trigger them
      this._requestExecutionPromiseResolve = resolve;

      try {
        this.setProcessingStatus(true);
        const config = {
          launchId: LiveStreaming.config.launchId,
          roomId: this.userId,
          peerId: this.peerId
        };

        const status = await this.instance.sendMediaStreamingStartRequest(config, this.requestedStreams);

        // failed to send media start request due to socket issue
        if (!status) {
          this.setProcessingStatus(false);
          resolve(false);
          return;
        }
        
      } catch (error) {
        this.setProcessingStatus(false);
        resolve(false);
        return;
      }

      // wait for the offer 
      const isOfferReceived = await this.waitForOfferTobeReceived();

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [peerId: ${this.peerId}] [Waiting for OFFER COMPLETED] [${isOfferReceived ? "SUCCESS": "FAILED"}]`
      );

      // stop execution if offer not received
      if (!isOfferReceived) {
        this.setProcessingStatus(false);
        resolve(false);
        return;
      } 

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [peerId: ${this.peerId}] [Waiting for REQUEST TO COMPLETE]`
      );

      // check if request is processed
      this.delayTimeoutId = setTimeout(() => {
        const result = this.isProcessed();

        this.setProcessingStatus(false);
        resolve(result);
      }, Request.requestTimeoutInSeconds * 1000);
    });
  }

  clear() {
    if (this.delayTimeoutId) {
      clearTimeout(this.delayTimeoutId);
      this.delayTimeoutId = null;
    }

    if (this._requestExecutionPromiseResolve) {
      this._requestExecutionPromiseResolve(true);
    }

    this.setProcessingStatus(false);
  }
}
